import { MetricName } from '@canvas/frontend-metrics';

import { defineMetric, type MetricMetadataBase } from '../define-metric';
import type { FrameState } from '../stabilized-scheduler';

export const SELECTIONS_OVERLAY_CONTAINER_ID = 'selections-overlay-container';

export const HIGHLIGHT_FOCUS_BOX_CLASS_NAME = 'highlight-focus-box';

export const FOCUS_BOX_DOM_ELEMENT_ID = 'selections-focus-box';

export const TABLE_CELL_IS_SELECTED_CLASS = 'isSelected';

export type CellSelectionProbeKind = 'legacyTdIsSelected' | 'focusBox';

export function isFocusBoxVisiblyRendered(element: HTMLElement): boolean {
  if (element.style.display === 'none') {
    return false;
  }

  const rect = element.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
}

export function getDomSingleCellSelectedFrameState(
  tableRoot: HTMLElement,
  entryId: string,
  fieldId: string
): FrameState {
  const td = tableRoot.querySelector<HTMLElement>(
    `td[data-entry-id="${entryId}"][data-field-id="${fieldId}"]`
  );

  if (!td || !td.classList.contains(TABLE_CELL_IS_SELECTED_CLASS)) {
    return { done: false, stabilityKey: null };
  }

  return { done: true, stabilityKey: `${entryId}:${fieldId}` };
}

function getVisibleFocusBoxElement(): HTMLElement | null {
  const byId = document.getElementById(FOCUS_BOX_DOM_ELEMENT_ID);

  if (byId && isFocusBoxVisiblyRendered(byId)) {
    return byId;
  }

  const overlay = document.getElementById(SELECTIONS_OVERLAY_CONTAINER_ID);

  if (!overlay) {
    return null;
  }

  const box = overlay.querySelector<HTMLElement>(`.${HIGHLIGHT_FOCUS_BOX_CLASS_NAME}`);

  if (!box || !isFocusBoxVisiblyRendered(box)) {
    return null;
  }

  return box;
}

export function getFocusBoxVisibleFrameState(): FrameState {
  const el = getVisibleFocusBoxElement();

  if (el) {
    return { done: true, stabilityKey: 'focus-box-visible' };
  }

  return { done: false, stabilityKey: null };
}

function rectsIntersectWithPositiveArea(a: DOMRectReadOnly, b: DOMRectReadOnly): boolean {
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.right, b.right);
  const top = Math.max(a.top, b.top);
  const bottom = Math.min(a.bottom, b.bottom);

  return left < right && top < bottom;
}

/**
 * For the pointer/sink `focusBox` probe (new selections): requires a visibly rendered focus
 * box and a positive-area intersection between that box and the target `td` identified by
 * `data-entry-id` / `data-field-id`. Legacy `isSelected` on `td` is not used. Success uses
 * stability key `${entryId}:${fieldId}` so completion is tied to the intended cell geometry.
 */
export function getFocusBoxVisibleForTargetCellFrameState(
  tableRoot: HTMLElement | null,
  entryId: string,
  fieldId: string
): FrameState {
  if (!tableRoot) {
    return { done: false, stabilityKey: null };
  }

  const focusEl = getVisibleFocusBoxElement();

  if (!focusEl) {
    return { done: false, stabilityKey: null };
  }

  const td = tableRoot.querySelector<HTMLElement>(
    `td[data-entry-id="${entryId}"][data-field-id="${fieldId}"]`
  );

  if (!td) {
    return { done: false, stabilityKey: null };
  }

  if (
    !rectsIntersectWithPositiveArea(
      focusEl.getBoundingClientRect(),
      td.getBoundingClientRect()
    )
  ) {
    return { done: false, stabilityKey: null };
  }

  return { done: true, stabilityKey: `${entryId}:${fieldId}` };
}

export interface CellSelectionProbeData {
  tableRoot: HTMLElement | null;
  selectionProbe: CellSelectionProbeKind;
  entryId: string;
  fieldId: string;
}

export interface CellSelectionMetadata extends MetricMetadataBase {
  databasesSelectionsPerformanceCohort: 'control' | 'test';
  selectionProbe: CellSelectionProbeKind;
}

export const cellSelectionMetric = defineMetric<
  CellSelectionProbeData,
  CellSelectionMetadata
>({
  metricName: MetricName.DATABASE_CELL_SELECTION_INTERACTION_TIME,
  buildProbe: (probeData) => {
    if (probeData.selectionProbe === 'legacyTdIsSelected') {
      if (!probeData.tableRoot) {
        return () => ({ done: false, stabilityKey: null });
      }

      const { tableRoot, entryId, fieldId } = probeData;

      return () => getDomSingleCellSelectedFrameState(tableRoot, entryId, fieldId);
    }

    const { tableRoot, entryId, fieldId } = probeData;

    return () => getFocusBoxVisibleForTargetCellFrameState(tableRoot, entryId, fieldId);
  },
  buildMetadata: (metadata) => ({
    interactionTimingSource:
      metadata.selectionProbe === 'legacyTdIsSelected'
        ? 'dom-single-cell-isSelected-sync'
        : 'selections-focus-box-visible',
    databasesSelectionsPerformanceCohort: metadata.databasesSelectionsPerformanceCohort,
  }),
});
